import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FJHorizontalScrollTab from '../../../common/components/FJTab/FJHorizontalScrollTab';
import FJVerticalScroller from '../../../common/components/FJScroller/FJVerticalScroller';

import FJStyleItem from '../FJStyleItem/FJStyleItem';
import FJLocalStore from '../../../store/FJLocalStore';
import FJConfig from '../../../config/FJConfig';
import { NEW_CLOTHES_CHANGER_STYLE_LIST } from '../../../config/dataConfig/FJClothesChangerData';

import styles from './FJClothesChangerStyleListV2.module.less';

const _isFullyVisible = (el, container) => {
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return elRect.top >= containerRect.top && elRect.bottom <= containerRect.bottom;
};

// 根据 tab 和 value 来获取分类的index
const _getCategoryIndexByValue = (gender, value) => {
    let result = 0;
    NEW_CLOTHES_CHANGER_STYLE_LIST[gender]?.map(({ clothes }, index) => {
        if (clothes.some(({ key }) => key === value)) result = index;
    });
    return result;
};

function FJClothesChangerStyleListV2({ onChange, value, id, tab }) {
    const [selectStyleCategory, setSelectStyleCategory] = useState(0);
    const scrollRef = useRef(null);
    const categoryRef = useRef([]);
    const _isScrollingRef = useRef(false);
    const _scrollingTimerRef = useRef(null);
    const _tabScrollRef = useRef(null);

    // 用于缓存选中的对象元素
    const selectedRef = useRef(null);

    // 每次更新 value 的时候也更新 ref
    useEffect(() => {
        selectedRef.current = {
            ...selectedRef.current,
            key: value?.key,
            category: selectStyleCategory,
        };
    }, [selectStyleCategory, value]);

    const styleTabList = useMemo(() => {
        if (tab === null) return [];

        const gender = tab === 0 ? 'female' : 'male';
        return (
            NEW_CLOTHES_CHANGER_STYLE_LIST[gender]?.map(({ categoryName }) => ({
                type: categoryName,
                text: categoryName,
            })) ?? []
        );
    }, [tab]);

    const styleDataList = useMemo(() => {
        if (tab === 0) {
            // female
            return NEW_CLOTHES_CHANGER_STYLE_LIST?.female;
        } else if (tab === 1) {
            // male
            return NEW_CLOTHES_CHANGER_STYLE_LIST?.male;
        }
    }, [tab]);

    const _onScroll = useCallback(() => {
        if (!scrollRef.current || !categoryRef.current || _isScrollingRef.current) return;
        const scrollDom = scrollRef.current.getScroller();
        const scrollRect = scrollDom.getBoundingClientRect();
        for (let i = 0; i < categoryRef.current.length; i++) {
            const rect = categoryRef.current[i]?.parentElement.getBoundingClientRect();
            if (rect && rect.top <= scrollRect.top && rect.bottom > scrollRect.top) {
                setSelectStyleCategory(i);
                break;
            }
        }
    }, []);

    // 统一的滚动函数，根据不同场景选择滚动策略
    const scrollToElement = useCallback((elementId, scrollToCategory = false, categoryIndex = null) => {
        if (!scrollRef.current || !categoryRef.current) return;

        // 延迟执行确保DOM更新完成
        const timer = setTimeout(() => {
            if (scrollToCategory && categoryIndex !== null) {
                // 滚动到分类标题
                const category = categoryRef.current[categoryIndex];
                if (category) {
                    const rect = category.getBoundingClientRect();
                    const scrollDom = scrollRef.current.getScroller();
                    const scrollRect = scrollDom.getBoundingClientRect();
                    const scrollTop = scrollDom.scrollTop + rect.top - scrollRect.top;
                    scrollRef.current.scrollTop(scrollTop, 'smooth');
                    setSelectStyleCategory(categoryIndex);
                }
            } else if (elementId) {
                // 滚动到具体元素
                const selectedEl = document.getElementById(elementId);
                if (selectedEl && !_isFullyVisible(selectedEl, scrollRef.current.getScroller())) {
                    selectedEl.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                    });
                }
            }

            _isScrollingRef.current = true;
            clearTimeout(_scrollingTimerRef.current);
            _scrollingTimerRef.current = setTimeout(() => {
                _isScrollingRef.current = false;
            }, 1000);
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    const _onTabChange = useCallback(
        (idx, shouldScroll = false) => {
            setSelectStyleCategory(idx);
            // 如果需要滚动，则滚动到对应分类
            if (shouldScroll) {
                scrollToElement(null, true, idx);
            }
        },
        [scrollToElement],
    );

    // 选中元素后的处理
    const handleSelect = useCallback(
        (item, index, callback) => {
            setSelectStyleCategory(index);
            selectedRef.current = { ...selectedRef.current, category: index };
            // 直接滚动到选中的元素
            scrollToElement(item.key);
            onChange?.(
                {
                    ...item,
                    filename: item.garmentFile,
                },
                callback,
            );
        },
        [onChange, scrollToElement],
    );

    // 处理tab变化和组件挂载时的滚动
    useEffect(() => {
        if (_tabScrollRef.current) {
            _tabScrollRef.current.updateArrowState();
        }

        if (!scrollRef.current || !categoryRef.current) return;

        // 优先使用当前value，如果没有则使用selectedRef
        const targetKey = value?.key || selectedRef.current?.key;
        if (!targetKey) return;

        // 找出对应分类的index
        let gender = 'female';
        if (tab === 1) gender = 'male';
        else if (tab === 2) gender = 'unisex';

        const index = _getCategoryIndexByValue(gender, targetKey);

        // 滚动到选中的元素
        scrollToElement(targetKey, false, index);
    }, [_onTabChange, tab, value?.key, scrollToElement]);

    return (
        <div id={id} className={classNames(styles.box)}>
            <FJHorizontalScrollTab
                dataList={styleTabList}
                selectedIndex={selectStyleCategory}
                space={6}
                onTabChange={idx => _onTabChange(idx, true)}
                className={styles.style_tab}
                itemClassName={styles.style_tab_item}
                selectClassName={styles.tab_select}
                lineClassName={styles.line}
                textClassName={styles.tab_text}
                ref={_tabScrollRef}
            />
            <FJVerticalScroller className={classNames(styles.scroll)} ref={scrollRef} onScroll={_onScroll}>
                {styleDataList?.length > 0 &&
                    styleDataList.map(({ categoryName, clothes }, index) => {
                        return (
                            <div key={categoryName}>
                                <div
                                    className={styles.category}
                                    id={categoryName}
                                    ref={ref => (categoryRef.current[index] = ref)}
                                >
                                    <span className={styles.category_name}>{FJLocalStore._(categoryName)}</span>
                                </div>
                                <div className={styles.list_box}>
                                    {clothes?.map(item => (
                                        <FJStyleItem
                                            key={item.key}
                                            id={item.key}
                                            imgSrc={FJConfig.stylePath + item.effectFile + `?v=${FJConfig.version}`}
                                            selected={item.key === value?.key}
                                            onClick={callback => {
                                                handleSelect(item, index, callback);
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
            </FJVerticalScroller>
        </div>
    );
}

FJClothesChangerStyleListV2.propTypes = {
    selectStyleCategory: PropTypes.number,
    list: PropTypes.array,
    onChange: PropTypes.func,
    value: PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.string,
        filename: PropTypes.string,
    }),
    setSelectStyleCategory: PropTypes.func,
    id: PropTypes.string,
    tab: PropTypes.number,
};

export default FJClothesChangerStyleListV2;
